// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import loadOrderStatusMappings from "@insite/client-framework/Store/Data/OrderStatusMappings/Handlers/LoadOrderStatusMappings";
import { getOrderStatusMappingDataView } from "@insite/client-framework/Store/Data/OrderStatusMappings/OrderStatusMappingsSelectors";
import { OrderModel, OrderSettingsModel } from "@insite/client-framework/Types/ApiModels";
import OrderDetailPageTypeLink from "@insite/content-library/Components/OrderDetailPageTypeLink";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import DataTableCell, { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import { DataTableCellBaseProps } from "@insite/mobius/DataTable/DataTableCellBase";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import { EMPTY_VALUE, sharedDateTimeOptions } from "@werner/XDL/constants";
import { ellipsis, xScreen } from "@werner/XDL/Utilities";
import { FC } from "react";
import React, { useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    order: OrderModel;
    orderSettings: OrderSettingsModel;
    extendedStyles?: OrderSummaryCardStyles;
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => {
    const orderStatusMappingDataView = getOrderStatusMappingDataView(state);

    return {
        language: getSession(state).language,
        shouldLoadOrderStatusMappings: !orderStatusMappingDataView.value && !orderStatusMappingDataView.isLoading,
    };
};

const mapDispatchToProps = {
    loadOrderStatusMappings,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & OwnProps;

export interface OrderSummaryCardStyles {
    orderSummaryCardGridItem?: GridItemProps;
    container?: GridContainerProps;
    pageTypeLinkGridItem?: GridItemProps;
    orderNumberGridItem?: GridItemProps;
    orderNumberHeadingAndText?: SmallHeadingAndTextStyles;
    statusGridItem?: GridItemProps;
    statusHeadingAndText?: SmallHeadingAndTextStyles;
    webOrderNumberGridItem?: GridItemProps;
    webOrderNumberHeadingAndText?: SmallHeadingAndTextStyles;
    totalGridItem?: GridItemProps;
    totalHeadingAndText?: SmallHeadingAndTextStyles;
    // XNG-Changes
    dataTableCells?: DataTableCellBaseProps;
    dataCell?: DataTableCellProps;
    shipToCell?: DataTableCellProps;
    customerPOCell?: DataTableCellProps;
}

export const orderSummaryCardStyles: OrderSummaryCardStyles = {
    orderSummaryCardGridItem: { width: 12 },
    container: { gap: 5 },
    pageTypeLinkGridItem: { width: 12 },
    orderNumberGridItem: { width: 6 },
    statusGridItem: { width: 6 },
    webOrderNumberGridItem: { width: 6 },
    totalGridItem: { width: 6 },
    shipToCell: {
        css: css`
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
        `,
    },
    customerPOCell: {
        css: css`
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
        `,
    },
};
const OrderSummaryCard: FC<Props> = ({
    language,
    order,
    shouldLoadOrderStatusMappings,
    loadOrderStatusMappings,
    extendedStyles,
}) => {
    const [styles] = useState(() => mergeToNew(orderSummaryCardStyles, extendedStyles));
    // XNG-Changes: change structure
    React.useEffect(() => {
        if (shouldLoadOrderStatusMappings) {
            loadOrderStatusMappings();
        }
    }, []);

    const orderDateDisplay = getLocalizedDateTime({
        dateTime: order.orderDate,
        language,
        options: {
            ...sharedDateTimeOptions,
        },
    });
    const orderNumber = order.erpOrderNumber || order.webOrderNumber;
    const shipToNickName = order.properties?.shipToNickName ? order.properties?.shipToNickName : order.stCompanyName;
    const customerNumber = order.customerSequence ? order.customerSequence : order.customerNumber;

    return (
        <>
            <DataTableCell {...styles.dataCell}>{orderDateDisplay && orderDateDisplay}</DataTableCell>
            <DataTableCell>
                {orderNumber && <OrderDetailPageTypeLink title={orderNumber} orderNumber={orderNumber} />}
            </DataTableCell>
            <DataTableCell {...styles.customerPOCell}>{`${order.customerPO || EMPTY_VALUE}`}</DataTableCell>
            <DataTableCell {...styles.shipToCell}>
                {`${shipToNickName} #${customerNumber}` || EMPTY_VALUE}
            </DataTableCell>
            <DataTableCell {...styles.dataCell}>{`${order.statusDisplay || EMPTY_VALUE}`}</DataTableCell>

            <DataTableCell {...styles.dataCell}>{`${order.orderGrandTotalDisplay || EMPTY_VALUE}`}</DataTableCell>
        </>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(OrderSummaryCard);
